import React from "react";
import {
  Button,
  Iconography,
  IconographySizes,
  IconographyThemes,
  InlineStatus,
  InlineStatusAppearance,
  InlineStatusIconType,
  Pagination,
  SearchInput,
} from "@hid-galaxy-ui/galaxy-react";
import moment from "moment";
import axios from "axios";
import { useNavigate } from "react-router";
import "./IncidentList.scss";
import IncidentFormModal from "../incident-form-modal";

interface Incident {
  id: string;
  title: string;
  technology: string;
  component: string;
  issueType: string;
  priority: string;
  environment: string;
  sdkAiVersion: string;
  subject: string;
  description: string;
  reportedDate: string;
  reportedBy: string;
}

const IncidentList: React.FC = () => {
  const [incidents, setIncidents] = React.useState<Incident[]>([]);
  const [searchTerm, setSearchTerm] = React.useState<string>("");
  const [currentPageNumber, setCurrentPageNumber] = React.useState<number>(1);
  const [pageSize, setPageSize] = React.useState<number>(10);
  const [addIncidentOpen, setAddIncidentOpen] = React.useState<boolean>(false);
  const [editIncident, setEditIncident] = React.useState<Incident | null>(null);
  const [editModalOpen, setEditModalOpen] = React.useState<boolean>(false);
  const [deleteModalOpen, setDeleteModalOpen] = React.useState<boolean>(false);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const navigate = useNavigate();

  // Fetch incidents from API
  const fetchIncidents = async () => {
    setIsLoading(true);
    try {
      console.log("📥 Fetching incidents from API...");
      const response = await axios.get(
        `${process.env.REACT_APP_NODE_AXIOS_BASEURL}api/incidents`
      );

      console.log("✅ Incidents fetched successfully:", response.data);

      // Extract data array from response (handles both { success, data } and direct array)
      const incidentsData = response.data.data || response.data;

      // Map API response to match local incident structure
      const fetchedIncidents = incidentsData.map((incident: any) => ({
        id: incident.incidentID?.toString() || incident.id?.toString(),
        title: incident.title || "",
        technology: incident.technology || "",
        component: incident.component || "",
        issueType: incident.issueType || "",
        priority: incident.priority || "",
        environment: incident.environment || "",
        sdkAiVersion: incident.sdkApiVersion || "",
        subject: incident.subject || "",
        description: incident.description || "",
        reportedDate:
          incident.updatedDate ||
          incident.createdDate ||
          incident.reportedDate ||
          new Date().toISOString(),
        reportedBy: incident.createdBy || incident.reportedBy || incident.updatedBy || "System",
      }));

      setIncidents(fetchedIncidents);

      // Also save to localStorage for backup
      localStorage.setItem("incidents", JSON.stringify(fetchedIncidents));
    } catch (error: any) {
      console.error("❌ Error fetching incidents:", error);

      // Fallback to localStorage if API fails
      const storedIncidents = localStorage.getItem("incidents");
      if (storedIncidents) {
        try {
          const parsedIncidents = JSON.parse(storedIncidents);
          setIncidents(Array.isArray(parsedIncidents) ? parsedIncidents : []);
          console.log("⚠️ Loaded incidents from localStorage as fallback");
        } catch (error) {
          console.error("Error parsing incidents from localStorage:", error);
          setIncidents([]);
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Load incidents from API on component mount
  React.useEffect(() => {
    fetchIncidents();
  }, []);

  // Load incidents from localStorage
  React.useEffect(() => {
    const loadIncidents = () => {
      const storedIncidents = localStorage.getItem("incidents");
      if (storedIncidents) {
        try {
          const parsedIncidents = JSON.parse(storedIncidents);
          setIncidents(Array.isArray(parsedIncidents) ? parsedIncidents : []);
        } catch (error) {
          console.error("Error parsing incidents from localStorage:", error);
          setIncidents([]);
        }
      }
    };

    loadIncidents();

    // Listen for storage changes (when incidents are added/updated in modal)
    window.addEventListener("storage", loadIncidents);

    // Custom event for same-window updates
    const handleIncidentsUpdate = () => {
      console.log("🔄 Incidents updated event received, refetching...");
      fetchIncidents();
    };
    window.addEventListener("incidentsUpdated", handleIncidentsUpdate);

    return () => {
      window.removeEventListener("storage", loadIncidents);
      window.removeEventListener("incidentsUpdated", handleIncidentsUpdate);
    };
  }, []);

  // Reload incidents when modal closes
  React.useEffect(() => {
    if (!addIncidentOpen && !editModalOpen && !deleteModalOpen) {
      console.log("🔄 Modal closed, refetching incidents...");
      fetchIncidents();
    }
  }, [addIncidentOpen, editModalOpen, deleteModalOpen]);

  const filteredIncidents = incidents
    .filter((incident) => {
      const matchesSearch =
        searchTerm.length === 0 ||
        incident.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        incident.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
        incident.technology?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        incident.component?.toLowerCase().includes(searchTerm.toLowerCase());

      return matchesSearch;
    })
    .sort((a, b) => {
      // Sort by reportedDate in descending order (latest first)
      const dateA = new Date(a.reportedDate).getTime();
      const dateB = new Date(b.reportedDate).getTime();
      return dateB - dateA;
    });

  const totalRecords = filteredIncidents.length;

  const handlePageChange = (clickedPageNumber: number | null) => {
    setCurrentPageNumber(clickedPageNumber);
  };

  const onPageSizesClick = (e: any, value: any) => {
    setPageSize(Number(value));
    setCurrentPageNumber(1);
  };

  const paginatedData = filteredIncidents.slice(
    (currentPageNumber - 1) * pageSize,
    currentPageNumber * pageSize
  );

  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case "Critical":
        return <span className="priority-badge critical">Critical</span>;
      case "High":
        return <span className="priority-badge high">High</span>;
      case "Medium":
        return <span className="priority-badge medium">Medium</span>;
      case "Low":
        return <span className="priority-badge low">Low</span>;
      default:
        return <span className="priority-badge">{priority}</span>;
    }
  };

  const handleRowClick = (incident: Incident) => {
    setEditIncident(incident);
    setEditModalOpen(true);
  };

  const handleEditClick = (incident: Incident) => {
    setEditIncident(incident);
    setEditModalOpen(true);
  };

  const handleDeleteClick = (incident: Incident) => {
    setEditIncident(incident);
    setDeleteModalOpen(true);
  };

  const handleAddIncident = () => {
    setAddIncidentOpen(true);
  };

  return (
    <div className="incident-list-wrapper">
      {/* <div className="page-header-dark">
        <h1>Incident Management</h1>
      </div> */}

      <div className="incident-stats-tile">
        <div className="tile-content">
          <div className="tile-label"> Total Incidents </div>
          <div className="value-circle">
            <div className="value">{incidents.length}</div>
            <div
              className="circle-out"
              style={{ backgroundColor: "#00AEEF47" }}
            >
              <div
                className="circle-in"
                style={{ backgroundColor: "#00AEEF" }}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="incident-list-container">
        <div className="incident-list-header">
          <div className="left">
            <h2>Incident Management</h2>
            <p className="subtitle">Track and manage all incidents</p>
          </div>
          <div className="right">
            {/* <Button
              variant="secondary"
              label="View Feature Comparison"
              icon={<Iconography icon="table" size={IconographySizes.Medium} />}
              onClick={() =>
                navigate("/hid-partner-admin/dashboard/feature-comparison")
              }
            /> */}
            <Button
              variant="secondary"
              label={isLoading ? "Loading..." : "Refresh"}
              disabled={isLoading}
              icon={
                <Iconography
                  icon="arrowsRotate"
                  size={IconographySizes.Medium}
                />
              }
              onClick={fetchIncidents}
            />
            <Button
              variant="primary"
              label="Add New Incident"
              icon={<Iconography icon="plus" size={IconographySizes.Medium} />}
              onClick={handleAddIncident}
            />
          </div>
        </div>

        <div className="search-input-wrapper">
          <SearchInput
            id="searchIncident"
            name="searchIncident"
            placeholder="Search by Title, Subject, Technology, or Component"
            isDisplayClearIcon={true}
            value={searchTerm}
            onChange={(event: any) => setSearchTerm(event.currentTarget.value)}
            onSearch={(event, value) => {
              setSearchTerm(value);
            }}
          />
        </div>

        <div className="incident-table-container">
          <div className="incident-table">
            <div className="table-header">
              {/* <div className="col col-title">Title</div> */}
              <div className="col col-subject">Subject</div>
              <div className="col col-technology">Technology</div>
              <div className="col col-issue-type">Issue Type</div>
              <div className="col col-priority">Priority</div>
              <div className="col col-reported">Reported By</div>
              <div className="col col-date">Reported Date</div>
              <div className="col col-actions">Actions</div>
            </div>

            {paginatedData.length > 0 ? (
              paginatedData.map((incident, index) => (
                <div className="table-row" key={incident.id}>
                  {/* <div className="col col-title" title={incident.title}>
                    {incident.title}
                  </div> */}
                  <div className="col col-subject" title={incident.subject}>
                    {incident.subject}
                  </div>
                  <div
                    className="col col-technology"
                    title={incident.technology}
                  >
                    {incident.technology || "-"}
                  </div>
                  <div
                    className="col col-issue-type"
                    title={incident.issueType}
                  >
                    {incident.issueType || "-"}
                  </div>
                  <div className="col col-priority">
                    {getPriorityBadge(incident.priority)}
                  </div>
                  <div className="col col-reported" title={incident.reportedBy}>
                    {incident.reportedBy}
                  </div>
                  <div className="col col-date">
                    {moment(incident.reportedDate).format("DD MMM, YYYY")}
                  </div>
                  <div className="col col-actions">
                    <div className="action-buttons">
                      <Button
                        variant="secondary"
                        className="incident-edit-button"
                        icon={
                          <Iconography
                            icon="userPen"
                            size={IconographySizes.Medium}
                            theme={IconographyThemes.Info}
                          />
                        }
                        onClick={() => handleEditClick(incident)}
                      />
                      <Button
                        variant="secondary"
                        className="incident-delete-button"
                        icon={
                          <Iconography
                            icon="trashCan"
                            size={IconographySizes.Medium}
                            theme={IconographyThemes.Error}
                          />
                        }
                        onClick={() => handleDeleteClick(incident)}
                      />
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="no-data">
                <div className="no-data-icon">
                  <Iconography
                    icon="fileCircleExclamation"
                    size={IconographySizes.XXLarge}
                  />
                </div>
                <h3>No Incidents Found</h3>
                <p>
                  {searchTerm
                    ? "No incidents match your search criteria. Try adjusting your search."
                    : "No incidents have been created yet. Click 'Add New Incident' to get started."}
                </p>
              </div>
            )}
          </div>

          {paginatedData.length > 0 && (
            <div className="pagination-wrapper">
              <Pagination
                currentPageNumber={currentPageNumber}
                totalRecords={totalRecords}
                pageSize={pageSize}
                onPageNumberClick={handlePageChange}
                onPageSizesClick={onPageSizesClick}
                pageSizes={[
                  { label: "10", value: "10" },
                  { label: "20", value: "20" },
                  { label: "50", value: "50" },
                ]}
              />
            </div>
          )}
        </div>

        {addIncidentOpen && (
          <IncidentFormModal setModalOpen={setAddIncidentOpen} mode="Add" />
        )}
        {editModalOpen && editIncident && (
          <IncidentFormModal
            incident={editIncident}
            setModalOpen={setEditModalOpen}
            mode="Update"
          />
        )}
        {deleteModalOpen && editIncident && (
          <IncidentFormModal
            incident={editIncident}
            setModalOpen={setDeleteModalOpen}
            mode="Delete"
          />
        )}
      </div>
    </div>
  );
};

export default IncidentList;
